import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { useSelector, useDispatch } from 'react-redux';
import { SimpleNavBar, LoadingSpinner, NotFound } from 'fni-components-next';
import { authSelectors, authActions } from '../authentication/modules/auth';
import { coreActions, coreSelectors } from '../redux/CoreSlice/core';
import AuthenticationRouter from '../authentication/AuthenticationRouter';
import allowedUnauthenticatedRoutes from './allowedUnauthRoutes';

const TenantsRouter = lazy(() => import('../tenant-admin/TenantsRouter.js'));
const UsersRouter = lazy(() => import('../user-admin/UsersRouter.js'));

export default function UnmigratedRouter() {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const currentAuthenticatedUser = useSelector(authSelectors.currentAuthenticatedUser);
    const currentSelectedTenant = useSelector(authSelectors.currentSelectedTenant);
    const canAccessTenantAdmin = useSelector(authSelectors.canAccessTenantAdmin);
    const isFNIAdmin = useSelector(authSelectors.isFNIAdmin);
    const authReady = useSelector(authSelectors.authReady);
    const tenantCode = useSelector(coreSelectors.tenantCode);
    const navLinks = useSelector(coreSelectors.navLinks);
    const { pathname } = useLocation();


    const handleSignOut = () => dispatch(authActions.signOutUser())
        .then(response => navigate('/auth/login/' + response?.payload));

    useEffect(() => {
        if(currentAuthenticatedUser && currentSelectedTenant) dispatch(coreActions.getNavLinks());
    }, [currentAuthenticatedUser, currentSelectedTenant]);

    // Redirect to appropriate default page based on user role
    useEffect(() => {
        // Wait for auth to be ready before making redirect decisions
        if (!authReady) return;

        if (currentAuthenticatedUser && pathname === '/') {
            // FNI Admins go to tenant admin, non-FNI tenant admins go to user admin
            if (isFNIAdmin) {
                navigate('/tenants/list/tenants');
            } else if (canAccessTenantAdmin) {
                navigate('/users/list/users');
            }
        }
    }, [authReady, currentAuthenticatedUser, canAccessTenantAdmin, isFNIAdmin, pathname]);

    // Redirect FNI admins from /users/* to /tenants/* equivalent paths
    useEffect(() => {
        if (!authReady || !currentAuthenticatedUser) return;

        if (isFNIAdmin && pathname.startsWith('/users/')) {
            // Redirect /users/list/* to /tenants/list/*
            const newPath = pathname.replace('/users/', '/tenants/');
            navigate(newPath);
        }
    }, [authReady, currentAuthenticatedUser, isFNIAdmin, pathname]);

    useEffect(() => {
        // Wait for auth to be ready before making redirect decisions
        if (!authReady) return;

        // Only redirect to login if user is not authenticated
        // Skip redirect for /auth/* paths as they handle their own auth
        if (!currentAuthenticatedUser && !allowedUnauthenticatedRoutes.includes(pathname)) {
            if (!pathname.startsWith('/auth/login/') && !pathname.startsWith('/auth/')) {
                navigate('/auth/login/' + tenantCode);
            }
        }
    }, [authReady, pathname, currentAuthenticatedUser, tenantCode]);


    return (
        <>
            <CssBaseline />
            <SimpleNavBar 
                currentAuthenticatedUser={currentAuthenticatedUser} onSignOut={handleSignOut} 
                code={tenantCode}
                links={navLinks} />
            <Suspense fallback={ <LoadingSpinner open={true} /> }>
                <Routes>
                    {/* Non-FNI users (tenant admins and regular users) use /users/* routes */}
                    { currentAuthenticatedUser && !isFNIAdmin ? <Route path='users/*' element={<UsersRouter />} /> : null }
                    {/* Tenant admins (both FNI and non-FNI) can access /tenants/* for tenant management */}
                    { currentAuthenticatedUser && canAccessTenantAdmin ? <Route path='tenants/*' element={<TenantsRouter />} /> : null }
                    <Route path='auth/*' element={<AuthenticationRouter />} />
                    <Route path='*' element={<NotFound />} />
                </Routes>
            </Suspense>
        </>
    );
}
